import { Field, Form, Formik } from 'formik'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FacebookLogin } from 'src/common/components/FacebookLogin'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { getAthlete, useRegisterMutation, facebookLogin } from 'src/services/mainAPI'
import { useAppSelector } from 'src/store/hooks'
import * as Yup from 'yup'

const SignupSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().required('Password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm your password'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required')
})

const Register = () => {
    const dispatch = useDispatch()
    const router = useRouter()
    const [register, { error }] = useRegisterMutation()
    const user = useAppSelector((state) => state.user.data)

    const handleFBLogin = (data) => {
        dispatch(
            facebookLogin.initiate({
                email: data.profile.email,
                username: data.profile.email,
                firstName: data.profile.first_name,
                lastName: data.profile.last_name,
                ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                facebookUserId: data.profile.id,
                fbAccessToken: data.tokenDetail.accessToken
            })
        )
    }

    useEffect(() => {
        if (user) {
            dispatch(getAthlete.initiate(user.userName))
            localStorage.setItem('user:profile', JSON.stringify(user))
            void router.replace('/dashboard-new')
        }
    }, [user])

    return (
        <AppLayout pageTitle="Register">
            <div className="min-h-screen relative">
                <section className="bg-white">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        <div className="relative flex items-start px-4 pb-10 pt-6 md:pt-40 sm:pb-16 md:justify-center lg:pb-24 bg-gray-50 sm:px-6 lg:px-8 min-h-[55vh] md:min-h-auto">
                            <div className="absolute inset-0">
                                <img
                                    className="object-cover object-top w-full h-full"
                                    src="https://res.cloudinary.com/yes-fit/image/upload/q_auto/v1636032859/manual/register-and-sign-up-for-yes-fit.jpg"
                                    alt="Register or Sign Up for Yes.Fit"
                                />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-b from-black to-transparent"></div>

                            <div className="relative">
                                <div className="w-full max-w-xl xl:w-full xl:mx-auto xl:pr-24 xl:max-w-xl">
                                    <h3 className="text-2xl md:text-6xl font-bold text-white">
                                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-yf-green to-purple-400 text-4xl">
                                            Your Fit. Your Way.
                                        </span>
                                        <br className="block" />
                                        Join over 400,000 users!
                                    </h3>
                                    <ul className="grid grid-cols-1 mt-4 md:mt-10 sm:grid-cols-2 gap-x-8 gap-y-4">
                                        <li className="flex items-center space-x-3">
                                            <div className="inline-flex items-center justify-center flex-shrink-0 w-5 h-5 bg-yf-green rounded-full">
                                                <svg
                                                    className="w-3.5 h-3.5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </svg>
                                            </div>
                                            <span className="text-sm md:text-lg font-medium text-white">
                                                {' '}
                                                Exciting & Engaging Virtual Races{' '}
                                            </span>
                                        </li>
                                        <li className="flex items-center space-x-3">
                                            <div className="inline-flex items-center justify-center flex-shrink-0 w-5 h-5 bg-yf-green rounded-full">
                                                <svg
                                                    className="w-3.5 h-3.5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </svg>
                                            </div>
                                            <span className="text-sm md:text-lg  font-medium text-white">
                                                {' '}
                                                Fun Flexible Fitness Challenges{' '}
                                            </span>
                                        </li>
                                        <li className="flex items-center space-x-3">
                                            <div className="inline-flex items-center justify-center flex-shrink-0 w-5 h-5 bg-yf-green rounded-full">
                                                <svg
                                                    className="w-3.5 h-3.5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </svg>
                                            </div>
                                            <span className="text-sm md:text-lg  font-medium text-white">
                                                {' '}
                                                Earn Rewards & Medals{' '}
                                            </span>
                                        </li>
                                        <li className="flex items-center space-x-3">
                                            <div className="inline-flex items-center justify-center flex-shrink-0 w-5 h-5 bg-yf-green rounded-full">
                                                <svg
                                                    className="w-3.5 h-3.5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                        clipRule="evenodd"
                                                    ></path>
                                                </svg>
                                            </div>
                                            <span className="text-sm md:text-lg  font-medium text-white">
                                                {' '}
                                                Positive, Motivational Community{' '}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center px-4 py-10 pt-6 md:pt-10 bg-white sm:px-6 lg:px-8 sm:py-16 lg:py-24">
                            <div className="xl:w-full xl:max-w-sm 2xl:max-w-md xl:mx-auto">
                                <h2 className="text-3xl font-bold leading-tight text-black sm:text-4xl">
                                    Register for Free
                                </h2>
                                <p className="font-light mb-4 mt-2">
                                    Join Yes.Fit and be a part of a movement that changes
                                    your mood, your life, as well as your body. We’re a
                                    team and a family that will cheer you on and keep you
                                    motivated.
                                </p>

                                <Formik
                                    initialValues={{
                                        email: '',
                                        // timezone: '',
                                        password: '',
                                        confirmPassword: '',
                                        firstName: '',
                                        lastName: ''
                                    }}
                                    validateOnBlur
                                    validationSchema={SignupSchema}
                                    onSubmit={async (values) => {
                                        // eslint-disable-next-line @typescript-eslint/await-thenable
                                        await register({
                                            email: values.email,
                                            username: values.email,
                                            firstName: values.firstName,
                                            ianaTimezone:
                                                Intl.DateTimeFormat().resolvedOptions()
                                                    .timeZone, // iana timezone,
                                            lastName: values.lastName,
                                            password: values.password
                                        })
                                    }}
                                >
                                    {({ isSubmitting }) => (
                                        <Form>
                                            <div className="bg-white smMd:shadow-sm smMd:rounded-lg smMd:max-w-xl mr-auto md:mr-0 smMd:ml-auto z-10 block">
                                                <div>
                                                    <div className="grid grid-cols-1 gap-3 md:gap-6">
                                                        <Field name="email">
                                                            {({
                                                                field, // { name, value, onChange, onBlur }
                                                                meta
                                                            }) => (
                                                                <label className="block">
                                                                    <input
                                                                        autoComplete="username"
                                                                        type="email"
                                                                        placeholder="Email"
                                                                        className="mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                                        {...field}
                                                                    />
                                                                    {meta.touched &&
                                                                        meta.error && (
                                                                            <span className="text-red-500 text-sm">
                                                                                {
                                                                                    meta.error
                                                                                }
                                                                            </span>
                                                                        )}
                                                                </label>
                                                            )}
                                                        </Field>

                                                        {/* <Field name="timezone">
                                            {({
                                                field, // { name, value, onChange, onBlur }
                                                meta
                                            }) => (
                                                <label className="block">
                                                    <input
                                                        type="text"
                                                        placeholder="(UTC-5:00) Eastern Time (US & Canada)"
                                                        className="mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                        {...field}
                                                    />
                                                    {meta.touched && meta.error && (
                                                        <span className="text-red-500 text-sm">
                                                            {meta.error}
                                                        </span>
                                                    )}
                                                </label>
                                            )}
                                        </Field> */}

                                                        <Field name="password">
                                                            {({
                                                                field, // { name, value, onChange, onBlur }
                                                                meta
                                                            }) => (
                                                                <label className="block">
                                                                    <input
                                                                        autoComplete="current-password"
                                                                        type="password"
                                                                        placeholder="Password"
                                                                        className="mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                                        {...field}
                                                                    />
                                                                    {meta.touched &&
                                                                        meta.error && (
                                                                            <span className="text-red-500 text-sm">
                                                                                {
                                                                                    meta.error
                                                                                }
                                                                            </span>
                                                                        )}
                                                                </label>
                                                            )}
                                                        </Field>

                                                        <Field name="confirmPassword">
                                                            {({
                                                                field, // { name, value, onChange, onBlur }
                                                                meta
                                                            }) => (
                                                                <label className="block">
                                                                    <input
                                                                        type="password"
                                                                        placeholder="Confirm Password"
                                                                        className="mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                                        {...field}
                                                                    />
                                                                    {meta.touched &&
                                                                        meta.error && (
                                                                            <span className="text-red-500 text-sm">
                                                                                {
                                                                                    meta.error
                                                                                }
                                                                            </span>
                                                                        )}
                                                                </label>
                                                            )}
                                                        </Field>

                                                        <Field name="firstName">
                                                            {({
                                                                field, // { name, value, onChange, onBlur }
                                                                meta
                                                            }) => (
                                                                <label className="block">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="First Name"
                                                                        className="mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                                        {...field}
                                                                    />
                                                                    {meta.touched &&
                                                                        meta.error && (
                                                                            <span className="text-red-500 text-sm">
                                                                                {
                                                                                    meta.error
                                                                                }
                                                                            </span>
                                                                        )}
                                                                </label>
                                                            )}
                                                        </Field>

                                                        <Field name="lastName">
                                                            {({
                                                                field, // { name, value, onChange, onBlur }
                                                                meta
                                                            }) => (
                                                                <label className="block">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Last Name"
                                                                        className="mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                                        {...field}
                                                                    />
                                                                    {meta.touched &&
                                                                        meta.error && (
                                                                            <span className="text-red-500 text-sm">
                                                                                {
                                                                                    meta.error
                                                                                }
                                                                            </span>
                                                                        )}
                                                                </label>
                                                            )}
                                                        </Field>

                                                        {error && (
                                                            <div className="block">
                                                                <p className="text-red-500 font-medium">
                                                                    {error}
                                                                </p>
                                                            </div>
                                                        )}

                                                        <div className="flex flex-col text-center mb-4">
                                                            <button
                                                                type="submit"
                                                                disabled={isSubmitting}
                                                                className="mb-1 py-5 bg-gradient-to-r from-yf-green to-yf-purple text-white font-medium rounded-lg focus:outline-none"
                                                            >
                                                                {isSubmitting
                                                                    ? 'Loading...'
                                                                    : 'Register'}
                                                            </button>
                                                        </div>

                                                        <div className="text-center">
                                                            <p>
                                                                Already have an account?{' '}
                                                                <Link href="/login">
                                                                    <a className="text-yf-green">
                                                                        Login
                                                                    </a>
                                                                </Link>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>

                                <div className="mt-3 space-y-3">
                                    {/* <button
                                        type="button"
                                        className="relative inline-flex items-center justify-center w-full px-4 py-4 text-base font-semibold text-gray-700 transition-all duration-200 bg-white border-2 border-gray-200 rounded-md hover:bg-gray-100 focus:bg-gray-100 hover:text-black focus:text-black focus:outline-none"
                                    >
                                        <div className="absolute inset-y-0 left-0 p-4">
                                            <svg
                                                className="w-6 h-6 text-rose-500"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                            >
                                                <path d="M20.283 10.356h-8.327v3.451h4.792c-.446 2.193-2.313 3.453-4.792 3.453a5.27 5.27 0 0 1-5.279-5.28 5.27 5.27 0 0 1 5.279-5.279c1.259 0 2.397.447 3.29 1.178l2.6-2.599c-1.584-1.381-3.615-2.233-5.89-2.233a8.908 8.908 0 0 0-8.934 8.934 8.907 8.907 0 0 0 8.934 8.934c4.467 0 8.529-3.249 8.529-8.934 0-.528-.081-1.097-.202-1.625z"></path>
                                            </svg>
                                        </div>
                                        Sign up with Google
                                    </button> */}

                                    <FacebookLogin
                                        onSuccess={(res) => handleFBLogin(res)}
                                        onError={(error) => alert(error)}
                                    />
                                    <p className="text-xs">
                                        By clicking on the Register or Login with Facebook
                                        button, you agree to the{' '}
                                        <Link href="/terms">
                                            <a className="text-yf-green">
                                                terms and conditions.
                                            </a>
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </AppLayout>
    )
}

export default Register
